import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  MaskedInput,
  TextInput,
  Checkbox,
} from 'uxd-design-system-components';
import isEmail from 'validator/lib/isEmail';
import getText from '../../assets/locale/locale';
import { LocaleContext } from '../../contexts';
import './AddContactFormModal.scss';

const AddContactFormModal = ({
  displayColdContactModal,
  setDisplayColdContactModal,
  sendResultsToFA,
  coldContactData,
  setColdContactData,
  faInfo,
}) => {
  const [firstNameError, setFirstNameError] = useState(null);
  const [lastNameError, setLastNameError] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [phoneError, setPhoneError] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isConsentCheckboxSelected, setIsConsentCheckboxSelected] =
    useState(false);
  const [showConsentError, setShowConsentError] = useState(false);
  const [isEmailMeCheckboxSelected, setIsEmailMeCheckboxSelected] =
    useState(false);
  const locId = useContext(LocaleContext);
  // this regex will match strings that contain letters, hyphens, and apostrophies
  // for example, "Edward-Jones" is valid input, as is "O'Neill"
  const nameRegex = /^[a-zA-ZÀ-ÿ ‘’´'-]*$/;
  // this regex will match strings that contain all white spaces (space, tab, etc.)
  const whiteSpaceRegex = /^\s*$/;
  // this regex will match strings that contain all digits (0-9);
  const digitsRegex = /[^0-9]/g;

  const faHeadshotImage = faInfo.faPhoto;
  const faFullName = `${faInfo.faFirstName} ${faInfo.faLastName}`;
  const defaultText = getText(
    locId,
    'Results',
    'defaultText',
    'NewContactModalForm'
  );

  const invalidFirstNameError =
    getText(locId, 'Results', 'firstNameError', 'NewContactModalForm') +
    getText(locId, 'Results', 'validNameChars', 'NewContactModalForm');

  const invalidLastNameError =
    getText(locId, 'Results', 'lastNameError', 'NewContactModalForm') +
    getText(locId, 'Results', 'validNameChars', 'NewContactModalForm');

  const invalidEmailError = getText(
    locId,
    'Results',
    'emailError',
    'NewContactModalForm'
  );

  const missingEmailError = getText(
    locId,
    'Results',
    'missingEmailError',
    'NewContactModalForm'
  );

  const invalidPhoneError = getText(
    locId,
    'Results',
    'phoneError',
    'NewContactModalForm'
  );

  const isValidFirstName = () => {
    if (
      firstName.length === 0 ||
      firstName.match(whiteSpaceRegex) ||
      !firstName.match(nameRegex)
    ) {
      setFirstNameError(invalidFirstNameError);
      return false;
    }
    setFirstName(firstName);
    setFirstNameError(null);
    setColdContactData({
      ...coldContactData,
      firstName: firstName.trim(),
    });
    return true;
  };
  const isValidLastName = () => {
    if (
      lastName.length === 0 ||
      lastName.match(whiteSpaceRegex) ||
      !lastName.match(nameRegex)
    ) {
      setLastNameError(invalidLastNameError);
      return false;
    }
    setLastName(lastName);
    setLastNameError(null);
    setColdContactData({
      ...coldContactData,
      lastName: lastName.trim(),
    });
    return true;
  };

  const isInValidEmail = (value) => value !== '' && !isEmail(value);

  const isInvalidPhone = (value) => value !== '' && value.length !== 10;

  const isValidEmailAndPhoneCombination = () => {
    const unmaskedPhoneNumber = phone.replace(digitsRegex, '');
    if (email === '' && unmaskedPhoneNumber === '') {
      setEmailError(invalidEmailError);
      setPhoneError(invalidPhoneError);
      return false;
    }
    if (isInValidEmail(email) && unmaskedPhoneNumber === '') {
      setEmailError(invalidEmailError);
      setPhoneError(null);
      return false;
    }
    if (email === '' && isInvalidPhone(unmaskedPhoneNumber)) {
      setEmailError(null);
      setPhoneError(invalidPhoneError);
      return false;
    }
    if (isEmail(email) && isInvalidPhone(unmaskedPhoneNumber)) {
      setEmailError(null);
      setPhoneError(invalidPhoneError);
      return false;
    }
    if (isInValidEmail(email) && unmaskedPhoneNumber.length === 10) {
      setEmailError(invalidEmailError);
      setPhoneError(null);
      return false;
    }
    if (isInValidEmail(email) && isInvalidPhone(unmaskedPhoneNumber)) {
      setEmailError(invalidEmailError);
      setPhoneError(invalidPhoneError);
      return false;
    }
    if (email === '' && isEmailMeCheckboxSelected) {
      setEmailError(missingEmailError);
      setPhoneError(null);
      return false;
    }

    setEmail(email);
    setEmailError(null);
    if (phone === '(') {
      setPhone('');
    } else {
      setPhone(phone);
    }
    setPhoneError(null);

    setColdContactData({
      ...coldContactData,
      email: email === '' ? null : email.trim(),
      phoneNumber:
        unmaskedPhoneNumber === '' ? null : unmaskedPhoneNumber.trim(),
    });

    return true;
  };

  const isConsentChecked = () => {
    if (!isConsentCheckboxSelected) {
      setShowConsentError(true);
      return false;
    }
    setShowConsentError(false);
    return true;
  };

  const shareButtonHandler = () => {
    let isValidForm = true;
    if (!isValidFirstName()) isValidForm = false;
    if (!isValidLastName()) isValidForm = false;
    if (!isValidEmailAndPhoneCombination()) {
      isValidForm = false;
    }
    if (!isConsentChecked()) isValidForm = false;
    if (isValidForm) {
      setDisplayColdContactModal(false);
      sendResultsToFA();
    }
  };

  const cancelButtonHandler = () => {
    setDisplayColdContactModal(false);
  };
  const headerTextHandler = () => {
    const baseHeaderText = getText(
      locId,
      'Results',
      'header',
      'NewContactModalForm'
    );
    let headerText;
    if (faInfo.faFirstName && faInfo.faLastName) {
      headerText = baseHeaderText.replace('%FA_NAME%', faFullName);
    } else {
      headerText = baseHeaderText.replace('%FA_NAME%', defaultText);
    }
    return headerText;
  };

  const consentCheckboxHandler = () => {
    let consentToFAContactTimestamp;
    if (!isConsentCheckboxSelected) {
      consentToFAContactTimestamp = new Date().toISOString();
      setIsConsentCheckboxSelected(true);
      setShowConsentError(false);
    } else {
      consentToFAContactTimestamp = null;
      setIsConsentCheckboxSelected(false);
    }
    setColdContactData({ ...coldContactData, consentToFAContactTimestamp });
  };

  const emailMeCheckboxHandler = () => {
    let emailResultsToContactTimestamp;
    if (!isEmailMeCheckboxSelected) {
      emailResultsToContactTimestamp = new Date().toISOString();
      setIsEmailMeCheckboxSelected(true);
    } else {
      emailResultsToContactTimestamp = null;
      setIsEmailMeCheckboxSelected(false);
    }
    setColdContactData({ ...coldContactData, emailResultsToContactTimestamp });
  };

  const buttons = [
    {
      text: getText(
        locId,
        'Results',
        'cancelButton',
        'NewContactModalForm'),
      onClick: cancelButtonHandler,
      styleVariant: 'secondary',
    },
    {
      text: getText(
        locId,
        'Results',
        'shareButton',
        'NewContactModalForm'),
      onClick: shareButtonHandler,
      styleVariant: 'primary',
    },
  ];

  return (
    <>
      <Dialog
        title=""
        open={displayColdContactModal}
        buttons={buttons}
        className="coldContactModal"
        onRequestClose={() => setDisplayColdContactModal(false)}
      >
        <div className="title-container">
          {faHeadshotImage ? (
            <div className="fa-headshot">
              <img
                src={`data:image/png;base64,${faHeadshotImage}`}
                alt=""
                role="presentation"
                data-testid="fa-headshot"
              />
            </div>
          ) : null}
          <h3>{headerTextHandler()}</h3>
        </div>
        <p
          className="add-contact-modal-paragraph"
          data-testid="add-contact-modal-paragraph"
        >
          {faInfo.faFirstName && faInfo.faLastName
            ? getText(
                locId,
                'Results',
                'paragraphText',
                'NewContactModalForm'
              ).replace('%FA_NAME%', faFullName)
            : getText(
                locId,
                'Results',
                'paragraphText',
                'NewContactModalForm'
              ).replace('%FA_NAME%', defaultText)}
        </p>
        <form onSubmit={shareButtonHandler}>
          <TextInput
            id="first-name"
            label={getText(
              locId,
              'Results',
              'firstNameLabel',
              'NewContactModalForm'
            )}
            name="first-name"
            value={firstName}
            type="text"
            required
            onChange={(text) => {
              setFirstName(text);
              setColdContactData({
                ...coldContactData,
                firstName: text.trim(),
              });
            }}
            onBlur={isValidFirstName}
            error={firstNameError}
          />
          <TextInput
            id="last-name"
            label={getText(
              locId,
              'Results',
              'lastNameLabel',
              'NewContactModalForm'
            )}
            name="last-name"
            value={lastName}
            type="text"
            onChange={(text) => {
              setLastName(text);
              setColdContactData({
                ...coldContactData,
                lastName: text.trim(),
              });
            }}
            onBlur={isValidLastName}
            error={lastNameError}
          />
          <TextInput
            id="email"
            label={getText(
              locId,
              'Results',
              'emailLabel',
              'NewContactModalForm'
            )}
            name="email"
            value={email}
            type="email"
            onChange={(text) => {
              setEmail(text);
              setColdContactData({
                ...coldContactData,
                email: text === '' ? null : text.trim(),
              });
            }}
            onBlur={() => {
              setColdContactData({
                ...coldContactData,
                email: email === '' ? null : email.trim(),
              });
            }}
            error={emailError}
          />
          <MaskedInput
            id="phone"
            label={getText(
              locId,
              'Results',
              'phoneLabel',
              'NewContactModalForm'
            )}
            name="phone"
            customMask={getText(
              locId,
              'Results',
              'customPhoneMask',
              'NewContactModalForm'
            )}
            customMaskHelperText={getText(
              locId,
              'Results',
              'customPhoneMaskHelperText',
              'NewContactModalForm'
            )}
            value={phone}
            type="phone"
            onChange={(text) => {
              setPhone(text);
              const unmaskedPhoneNumber = text.replace(digitsRegex, '');
              setColdContactData({
                ...coldContactData,
                phoneNumber:
                  unmaskedPhoneNumber === ''
                    ? null
                    : unmaskedPhoneNumber.trim(),
              });
            }}
            onBlur={() => {
              const unmaskedPhoneNumber = phone.replace(digitsRegex, '');
              setColdContactData({
                ...coldContactData,
                phoneNumber:
                  unmaskedPhoneNumber === ''
                    ? null
                    : unmaskedPhoneNumber.trim(),
              });
            }}
            error={phoneError}
          />
          <div className={showConsentError ? 'consent-checkbox-error' : ''}>
            <Checkbox
              checked={isConsentCheckboxSelected}
              id="consent"
              name="consent"
              label={getText(
                locId,
                'Results',
                'consentLabel',
                'NewContactModalForm'
              )}
              onChange={() => {
                consentCheckboxHandler();
              }}
            />
          </div>
          {showConsentError ? (
            <p className="consent-error">
              {getText(locId, 'Results', 'consentError', 'NewContactModalForm')}
            </p>
          ) : null}
          <Checkbox
            checked={isEmailMeCheckboxSelected}
            id="emailMe"
            name="emailMe"
            label={getText(
              locId,
              'Results',
              'emailMeLabel',
              'NewContactModalForm'
            )}
            onChange={() => {
              emailMeCheckboxHandler();
            }}
          />
        </form>
        <p className="add-phone-or-email-requirement">
          {getText(locId, 'Results', 'requiredLabel', 'NewContactModalForm')}
        </p>
        <p
          className="add-contact-modal-disclaimer"
          data-testid="add-contact-modal-disclaimer"
        >
          {getText(locId, 'Results', 'legalText', 'NewContactModalForm')}
        </p>
      </Dialog>
    </>
  );
};

AddContactFormModal.propTypes = {
  displayColdContactModal: PropTypes.bool.isRequired,
  setDisplayColdContactModal: PropTypes.func.isRequired,
  sendResultsToFA: PropTypes.func.isRequired,
  coldContactData: PropTypes.objectOf(PropTypes.string),
  setColdContactData: PropTypes.func.isRequired,
  faInfo: PropTypes.objectOf(PropTypes.string).isRequired,
};
AddContactFormModal.defaultProps = {
  coldContactData: {},
};

export default AddContactFormModal;
